import React, { useState } from "react";
import { Minus, Plus } from "lucide-react";
import { toast } from "../../../utils/appToast";

import { SEAT_MAX, changeRfpSeats, formatDate, formatMoney } from "../../../services/rfpBillingApi";
import Dialog from "./Dialog";

// "Premium supports up to 10 users at $99 per user / month." The stepper's
// minimum is however many seats are actually in use right now — the
// backend rejects a drop below that anyway (seats_below_occupied), this
// just keeps the UI from offering a value it would reject.
// What the Stripe page will charge for the added seats: the same formula the server uses
// (price x added seats x the share of the period still left), so the preview matches.
const proratedCents = (added, unitAmount, periodStart, periodEnd) => {
  const total = new Date(periodEnd) - new Date(periodStart);
  const left = Math.max(new Date(periodEnd) - Date.now(), 0);
  return Math.max(50, Math.ceil(total > 0 ? (added * unitAmount * left) / total : 0));
};

export default function ManageSeatsDialog({
  open,
  onClose,
  currentSeats,
  seatsUsed,
  unitAmount,
  periodStart,
  periodEnd,
  onChanged,
  initialSeats = currentSeats,
  zClass,
}) {
  const [seats, setSeats] = useState(initialSeats);
  const [saving, setSaving] = useState(false);

  const min = Math.max(1, seatsUsed);
  const max = SEAT_MAX;

  const handleUpdate = async () => {
    setSaving(true);
    try {
      const origin = window.location.origin;
      const result = await changeRfpSeats({
        seats,
        // target_seats tells the page to show "adding seats" until the webhook confirms the payment.
        successUrl: `${origin}/account/billing?billing=success&target_seats=${seats}`,
        cancelUrl: `${origin}/account/billing?billing=cancelled`,
      });
      if (result.checkout_url) {
        // Added seats are always paid on Stripe's page; they only count once it confirms.
        window.location.href = result.checkout_url;
        return;
      }
      toast.info(`Seats will drop to ${result.scheduled_seat_quantity} at your next renewal.`);
      onChanged?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Couldn't update seats — try again");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title="Manage seats" maxWidth={420} zClass={zClass}>
      <h2 className="text-[18px] font-semibold text-[#1A1A1A]">Manage seats</h2>
      <p className="mt-2 text-[13.5px] text-[#52525B]">
        Premium supports up to {SEAT_MAX} users at {formatMoney(unitAmount)} per user / month.
      </p>

      <div className="mt-4 flex items-center justify-between rounded-xl border border-[#ECE9EE] bg-[#FAF9FB] p-4">
        <span className="text-[14px] font-medium text-[#1A1A1A]">Users</span>
        <div className="flex items-center gap-3">
          <button
            type="button"
            aria-label="Remove a seat"
            disabled={seats <= min}
            onClick={() => setSeats((n) => Math.max(min, n - 1))}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-[#ECE9EE] text-[#1A1A1A] disabled:opacity-30"
          >
            <Minus size={15} />
          </button>
          <span className="w-5 text-center text-[16px] font-semibold text-[#1A1A1A] [font-variant-numeric:tabular-nums]">
            {seats}
          </span>
          <button
            type="button"
            aria-label="Add a seat"
            disabled={seats >= max}
            onClick={() => setSeats((n) => Math.min(max, n + 1))}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-[#ECE9EE] text-[#1A1A1A] disabled:opacity-30"
          >
            <Plus size={15} />
          </button>
        </div>
      </div>
      {seats <= min && seatsUsed > 1 && (
        <p className="mt-2 text-[12.5px] text-[#8A8A90]">
          To go below {seatsUsed} seats, remove members in Team Members first.
        </p>
      )}

      <div className="mt-4 flex items-center justify-between border-t border-[#ECE9EE] pt-4 text-[14px]">
        <span className="text-[#52525B]">New monthly total</span>
        <span className="font-semibold text-[#1A1A1A] [font-variant-numeric:tabular-nums]">{formatMoney(unitAmount * seats)}</span>
      </div>
      {seats > currentSeats && (
        <p className="mt-1 text-[12px] leading-[1.5] text-[#8A8A90]">
          You'll pay about {formatMoney(proratedCents(seats - currentSeats, unitAmount, periodStart, periodEnd), "USD", false)} on
          Stripe's page for the {seats - currentSeats} added seat{seats - currentSeats === 1 ? "" : "s"}, prorated to{" "}
          {formatDate(periodEnd)}. All {seats} seats then renew together.
        </p>
      )}
      {seats < currentSeats && (
        <p className="mt-1 text-[12px] leading-[1.5] text-[#8A8A90]">
          You keep all {currentSeats} seats until {formatDate(periodEnd)}, then renew with {seats}.
        </p>
      )}

      <div className="mt-6 flex justify-end gap-3">
        <button
          type="button"
          onClick={onClose}
          className="rounded-[9px] px-4 py-2 text-[14px] font-medium text-[#52525B]"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleUpdate}
          disabled={saving || seats === currentSeats}
          className="rounded-[9px] bg-[#A04A97] px-4 py-2 text-[14px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
        >
          {saving ? "Please wait…" : seats > currentSeats ? "Continue to payment" : "Update seats"}
        </button>
      </div>
    </Dialog>
  );
}
